import ComponentEvents from 'components/component-events';

/**
 * Компонент отвечает за включение / отключение прокрутки тела документа,
 * за добавление отступов справа для предотвращения скачков элементов.
 *
 * Принцип использования:
 * При необходимости запретить скролл на документе вызвать LayoutScroll.disable()
 * При необходимости разрешить скролл на документе вызвать LayoutScroll.enable()
 * Если в стеке ещё есть элементы скролл будет по прежнему запрещён
 *
 * LayoutScroll.disable(<имя "слоя"> - необязательно) - отключает прокрутку
 * при наличии элементов в стеке:
 * - на document.body вешается класс "modal-active"
 * - запрещается скролл
 * - добавляется padding-right: 17px на отслеживаемые элементы
 *
 * LayoutScroll.enable() - убирает один элемент из стека
 * при пустом стеке:
 * - с document.body снимается класс "modal-active"
 * - возвращается скролл
 * - убирается padding-right с отслеживаемых элементов
 *
 * LayoutScroll.track($element<HTMLElement>) - отслеживать элемент
 * при наличии элементов в стеке:
 * на элемент будет наложен padding-right
 */
function LayoutScroll(options = {}) {
    // наследование
    ComponentEvents.call(this, options);

    this.stack = [];
    this.elements = [];

    window.addEventListener('resize', this._onWindowResizeEvent.bind(this));
    this._onWindowResizeEvent();

    this.track(document.body);
}

// наследование
LayoutScroll.prototype = Object.create(ComponentEvents.prototype);

// цепочка прототипов
Object.defineProperty(LayoutScroll.prototype, 'constructor', {
    value: LayoutScroll,
    writable: true,
    enumerable: false,
});

/**
 * Отслеживание элемента
 * При отключении скролла элементу будет добавлен отступ справа (вместо скроллбара)
 * Элемент как правило должен быть position: fixed
 *
 * @param {HTMLElement} $element Отслеживаемый элемент
 */
LayoutScroll.prototype.track = function($element) {
    if (!$element) {
        return;
    }

    if (this.elements.indexOf($element) != -1) {
        return;
    }

    this.elements.push($element);

    if (this.disabled()) {
        this._apply($element);
    }
}

/**
 * Прекращение отслеживания элемента
 * @param {HTMLElement} $element Отслеживаемый элемент
 */
LayoutScroll.prototype.untrack = function($element) {
    const index = this.elements.indexOf($element);

    if (index != -1) {
        this._restore($element);
        this.elements.splice(index, 1);
    }
}

/**
 * Прекращение отслеживания элемента
 * @param {HTMLElement} $element Отслеживаемый элемент
 */
LayoutScroll.prototype.setTracking = function($element, state) {
    return this[state ? 'track' : 'untrack']($element);
}

/**
 * Отключение скролла
 * @param {String} name Метка / ключ / имя события, любая информация (для дебага)
 */
LayoutScroll.prototype.disable = function(name = 'default') {
    let disabledNow = false;
    if (!this.stack.length) {
        this.elements.forEach(this._apply.bind(this));
        document.body.classList.add('modal-active');
        disabledNow = true;
    }

    this.stack.push(name);
    this.emit('disable', name, disabledNow);
}

/**
 * Включение скролла
 */
LayoutScroll.prototype.enable = function(name = 'default') {
    const item = this.stack.pop();

    if (!this.stack.length) {
        this.elements.forEach(this._restore.bind(this));
        document.body.classList.remove('modal-active');
    }

    this.emit('enable', name);

    return item;
}

/**
 * Включение / отключение скролла
 * @param {Boolean} state
 */
LayoutScroll.prototype.setEnabled = function(state, name) {
    return this[state ? 'enable' : 'disable'](name);
}

/**
 * Активное состояние
 * @return {Boolean} true если скролл включен
 */
LayoutScroll.prototype.enabled = function() {
    return !this.stack.length;
}

/**
 * Активное состояние
 * @return {Boolean} true если скролл выключен
 */
LayoutScroll.prototype.disabled = function() {
    return !this.enabled();
}

LayoutScroll.prototype._apply = function($element) {
    $element.style.setProperty('padding-right', `${this._paddingRight}px`);
}

LayoutScroll.prototype._restore = function($element) {
    $element.style.removeProperty('padding-right');
}

LayoutScroll.prototype._onWindowResizeEvent = function() {
    this._paddingRight = window.innerWidth - document.body.clientWidth;
}

export default LayoutScroll;
